import { useMsalAuthentication } from '@azure/msal-react'
import { InteractionType, InteractionRequiredAuthError, type AuthError } from '@azure/msal-browser'
import { msalInstance, loginRequest } from '../lib/msalConfig'
import { AuthErrorScreen } from '../components/auth/AuthErrorScreen'
import { getRedirectError, clearRedirectError } from '../lib/authRedirectError'
import type { AuthErrorSignals } from '../lib/authErrors'

/** Le type MSAL est un signal STABLE, contrairement au texte du message : on le transporte. */
function toSignals(error: AuthError): AuthErrorSignals {
  return {
    errorCode: error.errorCode,
    errorMessage: error.errorMessage,
    interactionRequired: error instanceof InteractionRequiredAuthError,
  }
}

/**
 * Issue #43 — la redirection n'est plus relancée en aveugle.
 *
 * L'ancien code appelait `instance.loginRedirect()` dans un `useEffect` à CHAQUE montage : au retour
 * d'Entra en erreur, l'utilisateur restait non authentifié, retombait sur cette page (route `*` de
 * l'arbre non authentifié) et repartait en redirection — une boucle silencieuse, sans message.
 *
 * ⚠️ `useMsalAuthentication` **seul ne suffit pas** : son erreur arrive par `addEventCallback`, or
 * l'échec est émis pendant que `main.tsx` bloque encore le rendu (`inProgress !== None`), donc
 * avant que ce composant existe. L'erreur est captée en amont par `authRedirectError`, dont ce
 * composant lit le résultat. Voir le commentaire de tête de ce module pour la chaîne complète.
 */
export function LoginPage() {
  // Lu AVANT tout hook d'authentification : si une tentative vient d'échouer, il ne faut surtout
  // pas en relancer une — c'est précisément ce qui referme la boucle.
  const bootError = getRedirectError()
  if (bootError) {
    return (
      <AuthErrorScreen
        signals={toSignals(bootError)}
        onRetry={() => {
          clearRedirectError()
          void msalInstance.loginRedirect(loginRequest).catch(() => {
            // La redirection elle-même a échoué (interaction déjà en cours, popup bloquée…).
            // On ne masque rien : le prochain rendu réaffichera l'erreur retenue.
          })
        }}
      />
    )
  }

  return <LoginRedirector />
}

/**
 * Déclenche la connexion. Isolé dans son propre composant pour que le hook ne soit **jamais**
 * appelé quand une erreur est déjà retenue — le rendu conditionnel porte sur le composant, pas
 * sur l'appel de hook.
 */
function LoginRedirector() {
  // La télémétrie d'échec est émise au point de capture (`authRedirectError`), pas ici : c'est le
  // seul endroit que TOUS les échecs traversent, y compris ceux de l'arbre authentifié.
  const { error, login } = useMsalAuthentication(InteractionType.Redirect, loginRequest)

  if (error) {
    return (
      <AuthErrorScreen
        signals={toSignals(error)}
        onRetry={() => {
          void login(InteractionType.Redirect, loginRequest).catch(() => {
            // `login` ne pose pas l'erreur dans l'état du hook (contrairement à `acquireToken`) :
            // sans ce `catch`, un rejet passerait en promesse non gérée et le bouton resterait
            // sans effet visible. L'erreur affichée reste celle de la tentative précédente.
          })
        }}
      />
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4">
      <div className="max-w-sm w-full text-center">
        <h1 className="font-heading text-3xl text-primary mb-2">Pragmelis 365</h1>
        <p className="font-body text-sm text-muted-foreground mb-8">
          Moins de gaspillage. Plus de valeur. Votre IT.
        </p>
        <p className="font-body text-sm text-muted-foreground">
          Connexion en cours, redirection vers Microsoft…
        </p>
      </div>
    </div>
  )
}
